import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../common/Config";
import { toast } from "sonner";
import { Box, Typography, TextField, Button, Grid2, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Autocomplete } from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';
import { encryptPayload, decryptPayload } from '../../../utils/customerCrypto';
import { getCompanySettings, resolveCustomerApiEncrypt } from '../../../utils/companySettingsCache';

const ContraPrice = () => {
  const [productOptions, setProductOptions] = useState([]);
  const [productFilteredOptions, setProductFilteredOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [productInputValue, setProductInputValue] = useState("");
  const accessToken = localStorage.getItem("accessToken");
  const [newProductList, setNewProductList] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  /////////////////////////////////////////////////////////////////////
  const [data, setData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [assignedContractProducts, setAssignedContractProducts] = useState([]);
  const id = useParams();
  const navigateToCustomerList = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("token");
  /////////////////////////////////////////////////////////////////////
  const fetchPageData = async () => {
    const formData = new FormData();
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    formData.set("id", id.customerId);
    if (useEncrypt) {
      const enc = encryptPayload({ id: id.customerId });
      if (enc) {
        formData.append("encrypted_payload", enc);
      }
    }
    const apiUrl = `${config.API_URL}/ws_get_customer_details_id`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch Not Order Report");
      }
      const body = await response.json();
      const resolved = useEncrypt
        ? (decryptPayload(body?.encrypted_payload) ||
            decryptPayload(body?.encrypted_data) ||
            decryptPayload(body?.payload) ||
            body)
        : body;
      setData(resolved?.data);
    } catch (error) {
      console.error("api fail Error fetching Not Order Report:", error);
    }
  };

  const fetchProductData = async () => {
    const formData = new FormData();
    formData.append("customer_id", id.customerId);
    formData.append("platform", "web");
    formData.append("authorization", accessToken);
    formData.append("supplier_id", 0);
    const apiUrl = `${config.API_URL}/purchases/getProducts`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
      },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch Not Order Report");
      }

      const data = await response.json();
      //   console.log(data);
      const formattedOptions = data.products.map((item) => ({
        label: item.name + " - " + item.code,
        value: item.id,
      }));
      setProductOptions(formattedOptions);
      setProductFilteredOptions(formattedOptions);
      setProductData(data.products);
    } catch (error) {
      console.error("api fail Error fetching Not Order Report:", error);
    }
  };

  const fetchAssignedContractProducts = async () => {
    const formData = new FormData();
    formData.append("customer_id", id.customerId);
    const apiUrl = `${config.API_URL}/ws_get_customer_contra_products`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok)
        throw new Error("Failed to fetch assigned contract products");
      const data = await response.json();
      // Extract product IDs and set assignedContractProducts
      const assignedIds = (data.data || []).map((item) => item.id);
      setAssignedContractProducts(assignedIds);

      // Set tableRows with the fetched product data
      const rows = (data.data || []).map((item) => ({
        code: item.product_code,
        id: item.id,
        name: item.product_name,
        price: item.price, // You can use this if you want to prefill the price input
        size: item.size,
      }));
      setTableRows(rows);
    } catch (error) {
      setAssignedContractProducts([]);
      setTableRows([]);
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchProductData();
    fetchPageData();
    fetchAssignedContractProducts();
  }, []);

  //////////////////////////////////////////////////////////

  const productHandleChange = (event, selectedOption) => {
    if (selectedOption) {
      if (assignedContractProducts.includes(selectedOption.value)) {
        toast.error("Contract price already assigned for this product.");
        setProductValue(null);
        return;
      }
      setProductValue(selectedOption);
      fetchAddProductInfo(selectedOption.value);
    } else {
      setProductValue(null);
    }
  };

  const productHandleInputChange = (event, inputValue) => {
    setProductInputValue(inputValue);
    const newFilteredOptions = productOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    setProductFilteredOptions(newFilteredOptions);
  };

  const formatNumber = (num) => {
    return parseFloat(num).toFixed(2);
  };

  const fetchAddProductInfo = async (selectedOption) => {
    const formData = new FormData();

    if (selectedOption) {
      formData.append("product_id", selectedOption);
      const existingProduct = tableRows.find(
        (item) => item.id === selectedOption
      );
      if (existingProduct) {
        toast.error("Product already exists in the list.");
        return;
      }
    }

    const productData1 = productData.find((item) => item.id === selectedOption);
    const newRow = {
      code: productData1.code,
      id: productData1.id,
      name: productData1.name,
      size: productData1.size,
    };
    setTableRows((prevRows) => [...prevRows, newRow]);
  };

  const submitFormData = async () => {
    if (tableRows.length < 1) {
      toast.error("Please add a product.");
      return;
    }

    setIsSubmitting(true);
    const form = document.getElementById("addcontraform");
    const formData = new FormData(form);
    let a = formData.getAll("product_price[]");
    let nonEmptyPrices = a.filter((price) => price.trim() !== "");

    if (nonEmptyPrices.length !== tableRows.length) {
      toast.error("Please enter price for each product.");
      setIsSubmitting(false);
      return;
    }
    formData.append("customer_id", id.customerId);
    const apiSalesRouteUrl = `${config.API_URL}/add_customer_contraPrice`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Contra price added successfully.");
        setTimeout(() => navigateToCustomerList("/customers"), 1500);
      } else {
        setIsSubmitting(false);
      }
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveRow = (rowId) => {
    const updatedRows = tableRows.filter((row) => row.id !== rowId);
    setTableRows(updatedRows);
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={3} alignItems="center">
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography className="page-title">Contra Price</Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  label="Company Name"
                  name="supplier"
                  value={data.company || ''}
                  disabled
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Autocomplete
                  options={productFilteredOptions}
                  value={productValue}
                  onChange={productHandleChange}
                  onInputChange={productHandleInputChange}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      className="input-border"
                      {...params}
                      size="small"
                      placeholder="Select Product"
                      fullWidth
                    />
                  )}
                  clearOnEscape
                  clearOnBlur={false}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      minHeight: '40px',
                    }
                  }}
                  componentsProps={{
                    popper: {
                      sx: {
                        zIndex: 9999
                      }
                    }
                  }}
                />
              </Grid2>
            </Grid2>
          </Box>
          {/* <Typography variant="h6" sx={{ fontWeight: 600,px: 2 }}>
            Order Items
          </Typography> */}
          <form id="addcontraform">
            <TableContainer
            >
              <Table className="table-cell-border">
                <TableHead >
                  <TableRow className="table-cell-header">
                    <TableCell width="100px">Sr. No</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell width="120px">Price</TableCell>
                    <TableCell width="120px">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" className="commonColor" sx={{ py: 4}}>
                        No product added
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableRows.map((row, i) => (
                      <TableRow key={row.id} hover
                      className="table-cell-style"
                      >
                        <TableCell>
                          {i + 1}
                          <input
                            type="hidden"
                            name="product_id[]"
                            value={row.id}
                          />
                        </TableCell>
                        <TableCell>
                          {row.name} - {row.size}({row.code})
                        </TableCell>
                        <TableCell>
                          <TextField
                          className="input-border"
                            size="small"
                            name="product_price[]"
                            defaultValue={row.price || ""}
                            sx={{ 
                              width: '100%',
                              '& .MuiInputBase-root': {
                                height: '32px'
                              },
                              '& .MuiInputBase-input': {
                                py: 0.5,
                                fontSize: '0.875rem'
                              }
                            }}
                            inputProps={{
                              style: { textAlign: 'right' }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleRemoveRow(row.id)}
                            size="small"
                            sx={{ color: 'error.main' }}
                            title="Remove"
                          >
                            <Iconify icon="solar:trash-bin-trash-bold" width={20} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </form>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2}}>
            <Button
              variant="contained"
              onClick={submitFormData}
              disabled={isSubmitting}
              className="button-variant-contained"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};
export default ContraPrice;
